import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const CheckoutSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Countdown redirect to dashboard
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/dashboard');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ backgroundColor: 'var(--patreon-cream)' }}
    >
      <div className="max-w-md w-full">
        {/* Success Card */}
        <div
          className="rounded-2xl p-8 shadow-lg text-center"
          style={{
            backgroundColor: 'var(--patreon-card-bg)',
            border: '1px solid var(--patreon-card-border)',
          }}
        >
          {/* Success Icon */}
          <div className="mx-auto w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6">
            <CheckCircleIcon className="w-12 h-12 text-green-600" />
          </div>

          {/* Success Message */}
          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            Subscription Successful!
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            Thank you for becoming a patron. Your subscription is now active.
          </p>

          {/* Session Info (if available) */}
          {sessionId && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500 mb-1">Session ID</p>
              <p className="text-xs font-mono text-gray-700 break-all">
                {sessionId}
              </p>
            </div>
          )}

          {/* What's Next */}
          <div className="text-left mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              What's next?
            </h2>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-start">
                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>Access exclusive content from your creator</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>Check your email for a receipt</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>Manage your subscription in your dashboard</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full flex items-center justify-center px-6 py-3 rounded-full text-white font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
              style={{
                backgroundColor: 'var(--patreon-btn-primary-bg)',
              }}
            >
              Go to Dashboard
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </button>

            <button
              onClick={() => navigate('/dashboard/subscriptions')}
              className="w-full flex items-center justify-center px-6 py-3 rounded-full font-semibold transition-all duration-200 border-2"
              style={{
                borderColor: 'var(--patreon-btn-primary-bg)',
                color: 'var(--patreon-btn-primary-bg)',
              }}
            >
              View My Subscriptions
            </button>
          </div>

          {/* Auto-redirect notice */}
          <p className="text-sm text-gray-500 mt-6">
            Redirecting to dashboard in {countdown} seconds...
          </p>
        </div>

        {/* Support Link */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600">
            Need help?{' '}
            <button
              onClick={() => navigate('/support')}
              className="font-semibold hover:underline"
              style={{ color: 'var(--patreon-orange)' }}
            >
              Contact Support
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
